import { formatPrice } from "../../../../../../common/helpers/formatPrice";
import { extractCurrencyInfo } from "../../../../../../common/helpers/currency";
import type { RawCartItem } from "../../../../../../types";
import type {
  TransformedCartItem,
  TransformedCartItemVariation,
  TransformedQuantityLimits,
  TransformedCartItemTotals,
} from "../../../types/cartApiTypes";

// Define clear types for the dependencies
interface LanguageInfo {
  currentLanguage: string;
  shouldTranslate: boolean;
}

/**
 * Transforms a single raw cart item into a structured, display-ready format.
 * @param item - The raw cart item from the WC Store API.
 * @param languageInfo - An object containing language context for translations.
 * @returns A transformed cart item object.
 */
export const transformCartItem = (
  item: RawCartItem,
  languageInfo: LanguageInfo
): TransformedCartItem => {
  // Extract currency info from the item's price object
  const currencyInfo = extractCurrencyInfo(item.prices);

  let finalName = item.name;

  if (languageInfo.shouldTranslate && item.extensions.product_translations) {
    const translations = item.extensions.product_translations;
    const currentLang = languageInfo.currentLanguage;
    if (
      translations &&
      !Array.isArray(translations) &&
      translations[currentLang]?.name
    ) {
      finalName = translations[currentLang].name;
    }
  }

  let finalVariation: TransformedCartItemVariation[] = item.variation.map(
    (v) => ({ ...v })
  );
  if (
    item.extensions.enriched_attributes &&
    item.extensions.enriched_attributes.attributes.length > 0
  ) {
    finalVariation = item.extensions.enriched_attributes.attributes.map(
      (attr) => {
        const term = attr.terms[0];
        return {
          attribute:
            languageInfo.shouldTranslate && attr.translated_name
              ? attr.translated_name
              : attr.name,
          value:
            languageInfo.shouldTranslate && term.translated_name
              ? term.translated_name
              : term.name,
        };
      }
    );
  }

  const quantityLimits: TransformedQuantityLimits = {
    minimum: item.quantity_limits.minimum,
    maximum: item.quantity_limits.maximum,
    multipleOf: item.quantity_limits.multiple_of,
    editable: item.quantity_limits.editable,
  };

  const itemTotals: TransformedCartItemTotals = {
    lineSubtotal: formatPrice(item.totals.line_subtotal, currencyInfo),
    lineSubtotalTax: formatPrice(item.totals.line_subtotal_tax, currencyInfo),
    lineTotal: formatPrice(item.totals.line_total, currencyInfo),
    lineTotalTax: formatPrice(item.totals.line_total_tax, currencyInfo),
    rawLineSubtotal: item.totals.line_subtotal,
    rawLineSubtotalTax: item.totals.line_subtotal_tax,
    rawLineTotal: item.totals.line_total,
    rawLineTotalTax: item.totals.line_total_tax,
  };

  const calculatedLineTotalRaw =
    (parseInt(item.prices.price, 10) || 0) * item.quantity;

  // Get parentId from the extension, falling back to 0 if not present
  const parentId = item.extensions.cart_item_parent_id?.parent_id ?? 0;

  return {
    // --- 1. Core Identification ---
    key: item.key,
    id: item.id,
    parentId: parentId,
    type: item.type,
    name: finalName,
    permalink: item.permalink,
    sku: item.sku,
    // --- 2. Descriptive Content ---
    images: item.images,
    variation: finalVariation,
    shortDescription: item.short_description,
    description: item.description,
    // --- 3. Quantity & Limits ---
    quantity: item.quantity,
    quantityLimits,
    // --- 4. Pricing & Totals ---
    price: formatPrice(item.prices.price, currencyInfo),
    regularPrice: formatPrice(item.prices.regular_price, currencyInfo),
    salePrice: formatPrice(item.prices.sale_price, currencyInfo),
    rawPrice: item.prices.price,
    rawRegularPrice: item.prices.regular_price,
    rawSalePrice: item.prices.sale_price,
    totals: itemTotals,

    // --- 5. Calculated Totals (for intuitive UI display)
    calculatedLineTotal: String(calculatedLineTotalRaw),
    formattedCalculatedLineTotal: formatPrice(
      String(calculatedLineTotalRaw),
      currencyInfo
    ),
  };
};
